import React, { useState } from 'react';
import { ArrowUpRight, Ellipsis, GitCompareArrows, Pencil, X } from 'lucide-react';
import { CategoryPill, IconButton, Menu, Pill } from '../../../components/ui';
import { useTaxonomyColors } from '../../../hooks/useTaxonomyColors';
import { NetworkAnalysis, ROLE_DESCRIPTION, ROLE_LABEL } from '../graphAnalytics';
import { AssessPanel, AssessStream } from './AssessPanel';
import { InfluenceRadar } from './InfluenceRadar';
import {
  influentialTies,
  isStale,
  LOW_CONFIDENCE,
  LeveragePoint,
  QUADRANT_DESCRIPTION,
  QUADRANT_SINGULAR,
  topShare,
} from './influenceInsights';
import { humanize, InfluenceArea, InfluenceEntity, InfluenceModel } from './influenceModel';
import { AnimatedBar, areaColor, EntityAvatar, PanelSection, relativeTime } from './ui';

interface DossierProps {
  entity: InfluenceEntity;
  model: InfluenceModel;
  analysis: NetworkAnalysis | null;
  leverage: LeveragePoint | null;
  compareIds: string[];
  assess: AssessStream;
  reducedMotion: boolean;
  onClose: () => void;
  onSelect: (id: string) => void;
  onHover: (id: string | null) => void;
  onToggleCompare: (id: string) => void;
  onOpenProfile: (entity: InfluenceEntity) => void;
  onEditScores: (entity: InfluenceEntity) => void;
  onAccepted: () => void;
  /** Header title id for dialogs */
  titleId?: string;
  /** Inside a sheet that has its own close button */
  hideClose?: boolean;
}

export const Dossier: React.FC<DossierProps> = ({
  entity,
  model,
  analysis,
  leverage,
  compareIds,
  assess,
  reducedMotion,
  onClose,
  onSelect,
  onHover,
  onToggleCompare,
  onOpenProfile,
  onEditScores,
  onAccepted,
  titleId,
  hideClose = false,
}) => {
  const { colorOf, labelOf } = useTaxonomyColors();
  const [proposal, setProposal] = useState<Record<string, number> | null>(null);
  const metrics = analysis?.metrics.get(entity.id);
  const ties = influentialTies(analysis, entity.id, 5)
    .map((id) => model.byId.get(id))
    .filter((e): e is InfluenceEntity => !!e);
  const values: Record<string, number> = {};
  entity.scores.forEach((s) => (values[s.categoryId] = s.score));
  const comparing = compareIds.includes(entity.id);
  const categoryDomain = entity.type === 'organization' ? 'organization_type' : 'stakeholder_category';
  const categoryKey = entity.type === 'organization' ? entity.orgType : entity.category;
  const now = new Date();
  // Areas the entity holds first, then the rest
  const areaRows: Array<{ area: InfluenceArea; score: InfluenceEntity['scores'][number] | null }> = model.areas
    .map((area) => ({ area, score: entity.scoreByArea[area.id] || null }))
    .sort((a, b) => (b.score?.score ?? -1) - (a.score?.score ?? -1));

  return (
    <div className="flex h-full min-h-0 flex-col">
      <header className="flex flex-shrink-0 items-start gap-3 px-4 pb-3 pt-4">
        <EntityAvatar entity={entity} size="md" />
        <div className="min-w-0 flex-1">
          <h2 id={titleId} className="truncate text-[15px] leading-tight tracking-[-0.01em] text-ink">
            {entity.label}
          </h2>
          <p className="mt-0.5 truncate text-[12px] text-neutral-500">{entity.subtitle}</p>
          <div className="mt-1.5 flex flex-wrap items-center gap-1">
            {categoryKey && (
              <CategoryPill label={labelOf(categoryDomain, categoryKey) || humanize(categoryKey)} color={colorOf(categoryDomain, categoryKey)} />
            )}
            {leverage?.quadrant && (
              <Pill tone="outline" title={QUADRANT_DESCRIPTION[leverage.quadrant]}>
                {QUADRANT_SINGULAR[leverage.quadrant]}
              </Pill>
            )}
            {!entity.assessed && <Pill tone="amber">Unassessed</Pill>}
          </div>
        </div>
        <div className="-mr-1.5 flex flex-shrink-0 items-center">
          <IconButton label={comparing ? 'Remove from compare' : 'Compare'} size="sm" onClick={() => onToggleCompare(entity.id)} aria-pressed={comparing} className={comparing ? '!text-ink bg-neutral-100' : ''}>
            <GitCompareArrows className="h-4 w-4" strokeWidth={1.75} />
          </IconButton>
          <IconButton label="Open profile" size="sm" onClick={() => onOpenProfile(entity)}>
            <ArrowUpRight className="h-4 w-4" strokeWidth={1.75} />
          </IconButton>
          <Menu
            items={[{ label: 'Edit scores', icon: Pencil, onSelect: () => onEditScores(entity) }]}
            trigger={(props) => (
              <IconButton label="More" size="sm" {...props}>
                <Ellipsis className="h-4 w-4" strokeWidth={1.75} />
              </IconButton>
            )}
          />
          {!hideClose && (
            <IconButton label="Close" size="sm" onClick={onClose}>
              <X className="h-4 w-4" strokeWidth={1.75} />
            </IconButton>
          )}
        </div>
      </header>

      <div className="min-h-0 flex-1 space-y-6 overflow-y-auto overscroll-contain px-4 pb-6">
        {/* Headline numbers */}
        <div className="grid grid-cols-3 divide-x divide-neutral-200/70 rounded-xl border border-neutral-200">
          <Stat label="Average" value={entity.assessed ? entity.average : '—'} />
          <Stat
            label="Top area"
            value={
              entity.dominant ? (
                <span className="flex min-w-0 items-center gap-1.5">
                  <span className="h-2 w-2 flex-shrink-0 rounded-full" style={{ backgroundColor: areaColor(entity.dominant.color) }} />
                  <span className="tabular-nums">{entity.dominant.score}</span>
                </span>
              ) : (
                '—'
              )
            }
            hint={entity.dominant?.label}
          />
          <Stat label="Network" value={metrics && metrics.degree > 0 ? metrics.pct.influence : '—'} hint={metrics && metrics.degree > 0 ? topShare(metrics.pct.influence) : 'No ties'} />
        </div>

        {/* Profile */}
        <PanelSection title="Influence profile" aside={entity.lastUpdated ? `Updated ${relativeTime(entity.lastUpdated, now)}` : undefined}>
          {model.areas.length >= 3 && (
            <div className="mb-3">
              <InfluenceRadar
                areas={model.areas}
                values={values}
                proposal={proposal}
                reducedMotion={reducedMotion}
                label={`Influence profile of ${entity.label}`}
              />
              {proposal && (
                <p className="-mt-1 flex items-center justify-center gap-3 text-[11px] text-neutral-400">
                  <span className="flex items-center gap-1.5">
                    <span className="h-px w-4 bg-ink/80" /> Current
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="w-4 border-t border-dashed border-ink/60" /> Proposed
                  </span>
                </p>
              )}
            </div>
          )}
          <ul className="space-y-3">
            {areaRows.map(({ area, score }) => {
              const low = score?.confidence != null && score.confidence < LOW_CONFIDENCE;
              const stale = isStale(score?.updatedAt, now);
              const updated = relativeTime(score?.updatedAt, now);
              return (
                <li key={area.id}>
                  <div className="mb-1 flex items-center gap-2 text-[13px]">
                    <span className="h-2 w-2 flex-shrink-0 rounded-full" style={{ backgroundColor: areaColor(area.color) }} />
                    <span className={`min-w-0 flex-1 truncate ${score ? 'text-ink' : 'text-neutral-400'}`}>{area.label}</span>
                    {score?.source && (
                      <span
                        className={`rounded-full px-1.5 text-[10.5px] leading-4 ${score.source === 'ai' ? 'bg-neutral-900 text-white' : 'bg-neutral-100 text-neutral-500'}`}
                        title={score.source === 'ai' ? 'Accepted from an AI assessment' : 'Entered by an analyst'}
                      >
                        {score.source === 'ai' ? 'AI' : humanize(score.source)}
                      </span>
                    )}
                    <span className="w-7 text-right tabular-nums text-ink">{score ? score.score : <span className="text-neutral-300">—</span>}</span>
                  </div>
                  <AnimatedBar value={score?.score ?? 0} color={area.color} ghost={proposal && proposal[area.id] !== (score?.score ?? 0) ? proposal[area.id] : null} />
                  {score && (score.confidence != null || updated) && (
                    <p className="mt-1 flex items-center gap-2 text-[11.5px] text-neutral-400">
                      {score.confidence != null && (
                        <span className={low ? 'text-amber-700' : undefined} title="Analyst confidence in this score">
                          {score.confidence}% confidence
                        </span>
                      )}
                      {updated && (
                        <span className={stale ? 'text-amber-700' : undefined} title={score.updatedAt || undefined}>
                          {stale ? `Stale · ${updated}` : updated}
                        </span>
                      )}
                    </p>
                  )}
                </li>
              );
            })}
          </ul>
        </PanelSection>

        {/* Network position */}
        <PanelSection title="Network position" aside={metrics ? `${metrics.degree} direct ${metrics.degree === 1 ? 'tie' : 'ties'}` : undefined}>
          {metrics && metrics.degree > 0 ? (
            <>
              <div className="rounded-xl bg-neutral-50 px-3 py-2.5">
                <p className="text-[13px] text-ink">{ROLE_LABEL[metrics.role]}</p>
                <p className="mt-0.5 text-[12px] leading-snug text-neutral-500">{ROLE_DESCRIPTION[metrics.role]}</p>
              </div>
              <div className="mt-3 space-y-2">
                {[
                  { label: 'Influence', value: metrics.pct.influence, title: 'PageRank percentile — tied to well-tied actors' },
                  { label: 'Brokerage', value: metrics.pct.brokerage, title: 'Betweenness percentile — sits between groups' },
                  { label: 'Reach', value: metrics.pct.reach, title: 'Actors within two steps, percentile' },
                ].map((row) => (
                  <div key={row.label} className="flex items-center gap-3 text-[12px]" title={row.title}>
                    <span className="w-16 text-neutral-500">{row.label}</span>
                    <AnimatedBar value={row.value} color="#737373" height="h-1" className="flex-1" />
                    <span className="w-[86px] whitespace-nowrap text-right tabular-nums text-neutral-500">{Number.isFinite(row.value) ? topShare(row.value) : '—'}</span>
                  </div>
                ))}
              </div>
              {ties.length > 0 && (
                <>
                  <p className="mb-1 mt-4 text-[12px] text-neutral-400">Most influential ties</p>
                  <ul className="-mx-2">
                    {ties.map((tie) => (
                      <li key={tie.id}>
                        <button
                          type="button"
                          onClick={() => onSelect(tie.id)}
                          onMouseEnter={() => onHover(tie.id)}
                          onMouseLeave={() => onHover(null)}
                          className="flex w-full items-center gap-2.5 rounded-xl px-2 py-1.5 text-left transition-colors hover:bg-neutral-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40"
                        >
                          <EntityAvatar entity={tie} size="xs" />
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-[13px] text-ink">{tie.label}</span>
                            <span className="block truncate text-[11.5px] text-neutral-400">{tie.subtitle}</span>
                          </span>
                          {tie.dominant ? (
                            <span className="flex items-center gap-1.5 text-[12px] tabular-nums text-neutral-500" title={`${tie.dominant.label} ${tie.dominant.score}`}>
                              <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: areaColor(tie.dominant.color) }} />
                              {tie.average}
                            </span>
                          ) : (
                            <span className="text-[11px] text-neutral-300">—</span>
                          )}
                        </button>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </>
          ) : (
            <p className="text-[12.5px] text-neutral-500">No recorded ties — leverage through the network is unknown.</p>
          )}
        </PanelSection>

        {/* AI */}
        <PanelSection title="AI assessment">
          <AssessPanel entity={entity} areas={model.areas} stream={assess} onPreview={setProposal} onAccepted={onAccepted} />
        </PanelSection>
      </div>
    </div>
  );
};

const Stat: React.FC<{ label: string; value: React.ReactNode; hint?: React.ReactNode }> = ({ label, value, hint }) => (
  <div className="min-w-0 px-3 py-2">
    <p className="truncate text-[11px] text-neutral-500">{label}</p>
    <div className="mt-1 truncate text-[18px] leading-none tracking-[-0.02em] tabular-nums text-ink">{value}</div>
    {hint && <p className="mt-1 truncate text-[11px] text-neutral-400">{hint}</p>}
  </div>
);
